import type { DocumentBlock, DocumentPage, InlineNode, TreeNode } from "@/api/document/block";

interface PmMark {
	type: string;
}

export interface PmNode {
	type: string;
	attrs?: Record<string, any>;
	content?: PmNode[];
	text?: string;
	marks?: PmMark[];
}

const MARK_TYPES = ["bold", "underline", "italic"] as const;

function inlineToPm(node: InlineNode): PmNode {
	if (node.type === "field") {
		return { type: "field", attrs: { fieldId: node.attrs.fieldId } };
	}

	if (node.marks !== undefined && node.marks.length > 0) {
		return {
			type: "text",
			text: node.text,
			marks: node.marks.map((mark) => ({ type: mark.type })),
		};
	}

	return { type: "text", text: node.text };
}

function inlinesToPm(content: InlineNode[] | undefined) {
	if (content === undefined || content.length === 0) {
		return;
	}

	return content.map(inlineToPm);
}

function nodeToPm(node: Exclude<TreeNode, DocumentPage>): PmNode {
	switch (node.type) {
		case "heading":
			return {
				type: "heading",
				attrs: { id: node.attrs.id, level: node.attrs.level, align: node.attrs.align },
				content: inlinesToPm(node.content),
			};
		case "paragraph":
			return {
				type: "paragraph",
				attrs: { id: node.attrs.id, emphasis: node.attrs.emphasis, align: node.attrs.align },
				content: inlinesToPm(node.content),
			};
		case "list-item":
			return {
				type: "listItem",
				attrs: { id: node.attrs.id },
				content: inlinesToPm(node.content),
			};
		case "step":
			return {
				type: "step",
				attrs: { id: node.attrs.id, title: node.attrs.title },
				content: inlinesToPm(node.content),
			};
		case "table-cell":
			return {
				type: "tableCell",
				attrs: { id: node.attrs.id },
				content: inlinesToPm(node.content),
			};
		case "table-row":
			return {
				type: "tableRow",
				attrs: { id: node.attrs.id },
				content: node.content.map(nodeToPm),
			};
		case "table":
			return {
				type: "table",
				attrs: { id: node.attrs.id },
				content: node.content.map(nodeToPm),
			};
		case "variant-card":
			return {
				type: "variantCard",
				attrs: { id: node.attrs.id, variant: node.attrs.variant },
				content: node.content.map(nodeToPm),
			};
		case "page-break":
			return { type: "pageBreak", attrs: { id: node.attrs.id } };
		case "image":
			return {
				type: "image",
				attrs: {
					id: node.attrs.id,
					file: node.attrs.file,
					width: node.attrs.width,
					height: node.attrs.height,
					align: node.attrs.align,
				},
			};
		case "signature":
			return {
				type: "signature",
				attrs: { id: node.attrs.id },
				content: node.content.map(nodeToPm),
			};
		case "signature-row":
			return {
				type: "signatureRow",
				attrs: { id: node.attrs.id },
				content: node.content.map(nodeToPm),
			};
		case "signature-slot":
			return {
				type: "signatureSlot",
				attrs: { id: node.attrs.id },
				content: inlinesToPm(node.content),
			};
		case "pattern":
			return {
				type: "pattern",
				attrs: { id: node.attrs.id, patternId: node.attrs.patternId },
				content: node.content.map(nodeToPm),
			};
	}
}

function pmInlines(content: PmNode[] | undefined): InlineNode[] {
	const out: InlineNode[] = [];

	for (const node of content ?? []) {
		if (node.type === "field") {
			out.push({ type: "field", attrs: { fieldId: node.attrs?.fieldId } });

			continue;
		}

		const text = node.text ?? "";

		if (text.length === 0) {
			continue;
		}

		const marks = MARK_TYPES.filter((type) =>
			(node.marks ?? []).some((mark) => mark.type === type),
		).map((type) => ({ type }));

		if (marks.length > 0) {
			out.push({ type: "text", text, marks });

			continue;
		}

		out.push({ type: "text", text });
	}

	return out;
}

function pmId(node: PmNode) {
	const id = node.attrs?.id;

	if (typeof id !== "string" || id.length === 0) {
		return crypto.randomUUID();
	}

	return id;
}

function pmCell(node: PmNode): Extract<TreeNode, { type: "table-cell" }> {
	return { type: "table-cell", attrs: { id: pmId(node) }, content: pmInlines(node.content) };
}

function pmRow(node: PmNode): Extract<TreeNode, { type: "table-row" }> {
	return {
		type: "table-row",
		attrs: { id: pmId(node) },
		content: (node.content ?? []).map(pmCell),
	};
}

function pmSignatureSlot(node: PmNode): Extract<TreeNode, { type: "signature-slot" }> {
	return {
		type: "signature-slot",
		attrs: { id: pmId(node) },
		content: pmInlines(node.content),
	};
}

function pmSignatureRow(node: PmNode): Extract<TreeNode, { type: "signature-row" }> {
	return {
		type: "signature-row",
		attrs: { id: pmId(node) },
		content: (node.content ?? []).map(pmSignatureSlot),
	};
}

function pmBlock(node: PmNode): DocumentBlock | null {
	switch (node.type) {
		case "heading": {
			const attrs: Extract<DocumentBlock, { type: "heading" }>["attrs"] = {
				id: pmId(node),
				level: node.attrs?.level ?? 2,
			};

			if (node.attrs?.align === "center") {
				attrs.align = "center";
			}

			return { type: "heading", attrs, content: pmInlines(node.content) };
		}
		case "paragraph": {
			const attrs: Extract<DocumentBlock, { type: "paragraph" }>["attrs"] = { id: pmId(node) };
			const emphasis = node.attrs?.emphasis;

			if (emphasis === "paraphrase" || emphasis === "inspirational") {
				attrs.emphasis = emphasis;
			}

			if (node.attrs?.align === "center") {
				attrs.align = "center";
			}

			return { type: "paragraph", attrs, content: pmInlines(node.content) };
		}
		case "listItem":
			return { type: "list-item", attrs: { id: pmId(node) }, content: pmInlines(node.content) };
		case "step":
			return {
				type: "step",
				attrs: { id: pmId(node), title: node.attrs?.title ?? "" },
				content: pmInlines(node.content),
			};
		case "table":
			return {
				type: "table",
				attrs: { id: pmId(node) },
				content: (node.content ?? []).map(pmRow),
			};
		case "variantCard":
			return {
				type: "variant-card",
				attrs: { id: pmId(node), variant: node.attrs?.variant ?? "info" },
				content: (node.content ?? [])
					.map(pmBlock)
					.filter(
						(child): child is Extract<DocumentBlock, { type: "variant-card" }>["content"][number] =>
							child !== null && child.type !== "variant-card" && child.type !== "page-break",
					),
			};
		case "pageBreak":
			return { type: "page-break", attrs: { id: pmId(node) } };
		case "image": {
			const file = node.attrs?.file;
			const width = node.attrs?.width;
			const height = node.attrs?.height;

			if (typeof file !== "string" || file.length === 0 || !(width > 0) || !(height > 0)) {
				return null;
			}

			const attrs: Extract<DocumentBlock, { type: "image" }>["attrs"] = {
				id: pmId(node),
				file,
				width,
				height,
			};

			const align = node.attrs?.align;

			if (align === "center" || align === "right") {
				attrs.align = align;
			}

			return { type: "image", attrs };
		}
		case "signature":
			return {
				type: "signature",
				attrs: { id: pmId(node) },
				content: (node.content ?? []).map(pmSignatureRow),
			};
		case "pattern":
			return {
				type: "pattern",
				attrs: { id: pmId(node), patternId: node.attrs?.patternId },
				content: (node.content ?? [])
					.map(pmBlock)
					.filter(
						(child): child is Extract<DocumentBlock, { type: "pattern" }>["content"][number] =>
							child !== null && child.type !== "pattern" && child.type !== "page-break",
					),
			};
		default:
			return null;
	}
}

export const DocumentWire = {
	toDoc(blocks: DocumentBlock[]): PmNode {
		return { type: "doc", content: blocks.map(nodeToPm) };
	},

	toBlocks(doc: PmNode): DocumentBlock[] {
		return (doc.content ?? []).map(pmBlock).filter((block) => block !== null);
	},
};
